import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { CreditCard, Lock, Shield, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fireCheckoutPixels = (productData, selectedSize, selectedColor) => {
  if (!window.fbq) return;
  const payload = {
    content_name: productData.name,
    content_ids: [selectedColor + '-' + selectedSize],
    content_type: 'product',
    value: productData.price,
    currency: 'USD'
  };
  window.fbq('track', 'AddToCart', payload);
  window.fbq('track', 'InitiateCheckout', payload);
};

export const CheckoutChoiceModal = ({
  isOpen,
  onClose,
  selectedSize,
  selectedColor,
  boyshortsSize,
  boyshortsColor,
  quantity,
  productData
}) => {
  const [processing, setProcessing] = useState(null); // 'stripe' | 'paypal' | null
  const [error, setError] = useState('');

  const handleStripe = async () => {
    setProcessing('stripe');
    setError('');
    try {
      fireCheckoutPixels(productData, selectedSize, selectedColor);
      const { data } = await axios.post(`${API}/checkout/session`, {
        product_id: productData.id,
        size: selectedSize,
        color: selectedColor,
        boyshorts_size: boyshortsSize || null,
        boyshorts_color: boyshortsColor || null,
        quantity,
        origin_url: window.location.origin
      });
      if (!data.url) throw new Error('No checkout URL');
      window.location.href = data.url;
    } catch (e) {
      setProcessing(null);
      setError('Card checkout failed. Try PayPal or try again.');
    }
  };

  const handlePayPal = async () => {
    setProcessing('paypal');
    setError('');
    try {
      fireCheckoutPixels(productData, selectedSize, selectedColor);
      const { data } = await axios.post(`${API}/paypal/create-order`, {
        product_id: productData.id,
        size: selectedSize,
        color: selectedColor,
        boyshorts_size: boyshortsSize || null,
        boyshorts_color: boyshortsColor || null,
        quantity,
        origin_url: window.location.origin
      });
      if (!data.order_id) throw new Error('No order ID');
      window.location.href = `https://www.paypal.com/checkoutnow?token=${data.order_id}`;
    } catch (e) {
      setProcessing(null);
      setError('PayPal checkout failed. Try card instead.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !processing) onClose(); }}>
      <DialogContent
        data-testid="checkout-choice-modal"
        className="max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl"
      >
        <button
          onClick={() => !processing && onClose()}
          data-testid="checkout-choice-close"
          className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-1.5 hover:bg-white transition-colors disabled:opacity-40"
          disabled={!!processing}
          aria-label="Close checkout choice"
        >
          <X className="w-4 h-4 text-gray-700" />
        </button>

        <div className="bg-gradient-to-b from-[#7d1f3d] to-[#5d1729] text-white px-6 py-5">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-xl font-bold text-white">
              How would you like to pay?
            </DialogTitle>
            <DialogDescription className="text-white/85 text-sm">
              Snatched Duo Bundle + Free Boyshorts — <span className="font-bold text-[#d4af37]">${productData.price.toFixed(2)}</span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-3 bg-white">
          <Button
            onClick={handleStripe}
            disabled={!!processing}
            data-testid="checkout-choice-stripe"
            className="w-full bg-gray-900 hover:bg-black text-white text-base py-6 rounded-xl font-bold shadow-lg transform hover:scale-[1.02] transition-all"
          >
            {processing === 'stripe' ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to Checkout…
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Pay with Card
              </span>
            )}
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <Button
            onClick={handlePayPal}
            disabled={!!processing}
            data-testid="checkout-choice-paypal"
            className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white text-base py-6 rounded-xl font-bold shadow-lg transform hover:scale-[1.02] transition-all"
          >
            {processing === 'paypal' ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to PayPal…
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.68l-.04.22-.63 3.993-.028.14a.806.806 0 01-.795.68H7.792a.538.538 0 01-.531-.612l.047-.267 1.004-6.355.03-.176a.805.805 0 01.795-.681h1.643c3.238 0 5.774-1.314 6.514-5.12.313-1.607.22-2.923-.504-3.9-.152-.206-.326-.392-.52-.56a6.314 6.314 0 00-1.054-.622 9.09 9.09 0 00-.61-.26c-.382-.137-.8-.237-1.25-.307-.238-.037-.488-.06-.748-.073-.165-.008-.335-.012-.51-.012H7.937a.806.806 0 00-.795.681l-1.47 9.322a.538.538 0 00.531.612h2.894l.726-4.597.029-.176a.805.805 0 01.795-.681h.828c1.869 0 3.332-.76 3.761-2.958.178-.913.114-1.677-.226-2.272-.153-.268-.357-.494-.608-.68z" />
                </svg>
                Pay with PayPal
              </span>
            )}
          </Button>

          {error && (
            <p
              data-testid="checkout-choice-error"
              className="text-sm text-[#7d1f3d] bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-center"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-center gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Lock className="w-3.5 h-3.5" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Shield className="w-3.5 h-3.5" />
              <span>30-day guarantee</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
